import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react'
import { router } from '@inertiajs/react'

export interface CartItem {
  product_id: number
  variant_id?: number | null
  product_name: string
  variant_name?: string | null
  unit_price_cents: number
  tax_rate?: number
  quantity: number
  track_inventory?: boolean
  stock_quantity?: number | null
  discount_cents?: number
  promotions?: { name: string; tier_quantity: number; tier_price_cents: number }[]
}
export interface Cart { items: CartItem[]; total_discount_cents?: number }

interface CartContextValue {
  items: CartItem[]; count: number; totalCents: number; totalWithTax: number
  addItem: (pid: number, o?: { variantId?: number | null; quantity?: number }) => void
  removeItem: (idx: number) => void
  updateQuantity: (idx: number, q: number) => void
  clear: () => void
}
const Ctx = createContext<CartContextValue | null>(null)

export function CartProvider({ children, cart, slug }: { children: ReactNode; cart?: Cart; slug: string }) {
  const init = cart?.items ?? []
  const [items, setItems] = useState<CartItem[]>(init)
  useEffect(() => { if (slug) (window as any)._esmiposSlug = slug }, [slug])
  const [lastInit, setLastInit] = useState(() => JSON.stringify(init))
  const currentInit = JSON.stringify(init)
  if (currentInit !== lastInit) { setItems(init); setLastInit(currentInit) }
  const count = useMemo(() => items.reduce((s,i) => s + i.quantity, 0), [items])
  const totalCents = useMemo(() => {
    const subtotal = items.reduce((s,i) => s + i.unit_price_cents * i.quantity, 0)
    const discount = items.reduce((s,i) => s + (i.discount_cents ?? 0), 0)
    return subtotal - discount
  }, [items])
  const totalWithTax = useMemo(() => {
    const subtotal = items.reduce((s,i) => s + i.unit_price_cents * i.quantity, 0)
    const discount = items.reduce((s,i) => s + (i.discount_cents ?? 0), 0)
    const discountedSub = subtotal - discount
    const tax = items.reduce((s, i) => {
      const rate = i.tax_rate ?? 15
      const lineSubtotal = i.unit_price_cents * i.quantity
      const lineDiscounted = lineSubtotal - (i.discount_cents ?? 0)
      return s + (lineSubtotal > 0 ? Math.round(lineDiscounted * (rate / 100)) : 0)
    }, 0)
    return discountedSub + tax
  }, [items])
  const addItem = useCallback((pid: number, o: any = {}) => {
    const q = o.quantity ?? 1
    setItems(p => {
      const idx = p.findIndex(i => i.product_id === pid && (i.variant_id ?? null) === (o.variantId ?? null))
      if (idx >= 0) { const n = [...p]; n[idx] = { ...n[idx], quantity: n[idx].quantity + q }; return n }
      return [...p, { product_id: pid, variant_id: o.variantId ?? null, product_name: '...', unit_price_cents: 0, quantity: q }]
    })
    router.post(`/${slug}/catalog/cart`, { product_id: pid, variant_id: o.variantId ?? null, quantity: q }, { preserveScroll: true, preserveState: true, only: [] })
  }, [slug])
  const removeItem = useCallback((idx: number) => {
    setItems(p => p.filter((_,i) => i !== idx))
    router.delete(`/${slug}/catalog/cart/${idx}`, { preserveScroll: true, preserveState: true })
  }, [slug])
  const updateQuantity = useCallback((idx: number, q: number) => {
    if (q <= 0) setItems(p => p.filter((_,i) => i !== idx))
    else setItems(p => p.map((i,ii) => ii === idx ? { ...i, quantity: q } : i))
    router.patch(`/${slug}/catalog/cart/${idx}`, { quantity: q }, { preserveScroll: true, preserveState: true })
  }, [slug])
  const clear = useCallback(() => setItems([]), [])
  return <Ctx.Provider value={{ items, count, totalCents, totalWithTax, addItem, removeItem, updateQuantity, clear }}>{children}</Ctx.Provider>
}

export function useCart(): CartContextValue {
  const c = useContext(Ctx)
  if (!c) throw Error('useCart must be used within a CartProvider')
  return c
}
